import { useSyncExternalStore } from "react";

/**
 * Tracks unsaved work across the app (WritingPanel drafts, test-attempt
 * drafts) so the shell can warn before the tab closes (WP-08) and so a
 * pending sign-in redirect or test submission can force a best-effort flush
 * first (WP-09/WP-12). Mirrors saveStatus.ts's useSyncExternalStore pattern.
 */

type Flusher = () => Promise<boolean>;

const dirtyKeys = new Set<string>();
const flushers = new Map<string, Flusher>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

/**
 * Marks `key` dirty and returns a release function. Callers MUST release on
 * unmount (and on successful save) — a release that never fires is a
 * stuck-dirty leak that would prompt the user forever on every tab close.
 * Safe to call more than once for the same key; only the first call's
 * release actually clears it (later releases for the same key no-op), so
 * callers that guard re-entry with a ref (as useAutosave does) are safe by
 * construction.
 */
export function markDirty(key: string): () => void {
  dirtyKeys.add(key);
  emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    dirtyKeys.delete(key);
    emit();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return dirtyKeys.size > 0;
}

export function useIsDirty(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Plain-function snapshot for non-React callers (the beforeunload guard). */
export function hasDirtyWork(): boolean {
  return dirtyKeys.size > 0;
}

/**
 * Registers a "save whatever is currently pending for `key`" callback that
 * flushAll() can invoke on demand (tab hidden, pre-redirect, test submit).
 * The callback must never throw — it should resolve `true` on success (or
 * if there was nothing to save) and `false` on failure. Returns an
 * unregister function; consumers MUST call it on unmount so flushAll()
 * never invokes a stale closure over unmounted state.
 */
export function registerFlusher(key: string, flush: Flusher): () => void {
  flushers.set(key, flush);
  return () => {
    if (flushers.get(key) === flush) flushers.delete(key);
  };
}

/**
 * Best-effort: runs every registered flusher concurrently and waits for all
 * of them, bounded by `timeoutMs` when given. Returns `true` only if every
 * flusher resolved `true` before the timeout; `false` if any failed or the
 * timeout elapsed first. A `false` result does not mean the data is lost —
 * a slow save may still land — it means the caller should not treat the
 * flush as confirmed (e.g. should not redirect away / should abort submit).
 */
export async function flushAll(timeoutMs?: number): Promise<boolean> {
  const pending = Array.from(flushers.values());
  if (pending.length === 0) return true;

  const runAll = async () => {
    const results = await Promise.all(pending.map((flush) => flush().catch(() => false)));
    return results.every(Boolean);
  };

  if (timeoutMs === undefined) return runAll();

  return Promise.race([
    runAll(),
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ]);
}
